import React, { Children, isValidElement } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkDirective from 'remark-directive';
import { Link } from 'react-router-dom';
import remarkVeritage from './remarkVeritage';
import CodeBlock from '../components/CodeBlock';
import { Info, Warn, Bulb, ArrowRight } from '../components/Icons';

const CALLOUT_ICONS = { info: Info, warn: Warn, warning: Warn, danger: Warn, tip: Bulb };

function Callout({ type = 'info', title, children }) {
  const Icon = CALLOUT_ICONS[type] || Info;
  return (
    <div className={`docs-callout docs-callout--${type}`}>
      <span className="docs-callout-icon"><Icon size={18} /></span>
      <div className="docs-callout-body">
        {title && <div className="docs-callout-title">{title}</div>}
        {children}
      </div>
    </div>
  );
}

function Endpoint({ method = 'GET', path, auth }) {
  return (
    <div className="docs-endpoint">
      <span className={`docs-method docs-method--${String(method).toLowerCase()}`}>{method}</span>
      <code className="docs-endpoint-path">{path}</code>
      {auth && <span className="docs-endpoint-auth">{auth}</span>}
    </div>
  );
}

function hasCardDesc(node) {
  if (!node) return false;
  if (node.properties?.className?.includes?.('docs-card-desc')) return true;
  return (node.children || []).some(hasCardDesc);
}

function textOf(node) {
  if (!node) return '';
  if (node.type === 'text') return node.value;
  return (node.children || []).map(textOf).join('');
}

/** Resolves relative doc links ("create-session") against the current product/version. */
function DocLink({ href = '', basePath, children, ...rest }) {
  if (/^(https?:)?\/\//.test(href) || href.startsWith('mailto:')) {
    return <a href={href} target="_blank" rel="noreferrer" {...rest}>{children}</a>;
  }
  if (href.startsWith('#')) return <a href={href} {...rest}>{children}</a>;
  const to = href.startsWith('/') ? href : `${basePath}/${href}`;
  return <Link to={to} {...rest}>{children}</Link>;
}

export default function Markdown({ content, basePath = '' }) {
  return (
    <div className="docs-prose">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkDirective, remarkVeritage]}
        components={{
          'vt-callout':    ({ node, type, title, children }) => <Callout type={type} title={title}>{children}</Callout>,
          'vt-endpoint':   ({ node, method, path, auth }) => <Endpoint method={method} path={path} auth={auth} />,
          'vt-steps':      ({ children }) => <ol className="docs-steps">{children}</ol>,
          'vt-step':       ({ children }) => <li className="docs-step"><div className="docs-step-body">{children}</div></li>,
          'vt-step-title': ({ children, id }) => <div className="docs-step-title" id={id}>{children}</div>,
          a: ({ node, href, children, ...rest }) => <DocLink href={href} basePath={basePath} {...rest}>{children}</DocLink>,
          table: ({ node, children }) => <div className="docs-table-wrap"><table className="docs-table">{children}</table></div>,
          img: ({ node, src, alt }) => (
            <figure className="docs-figure">
              <img src={src} alt={alt || ''} loading="lazy" />
              {alt && <figcaption>{alt}</figcaption>}
            </figure>
          ),
          pre: ({ node, children }) => {
            const codeNode = node?.children?.[0];
            const cls = (codeNode?.properties?.className || []).find((c) => String(c).startsWith('language-'));
            const lang = cls ? String(cls).slice('language-'.length) : '';
            const meta = codeNode?.data?.meta || '';
            const title = (meta.match(/title="([^"]+)"/) || meta.match(/title=(\S+)/) || [])[1];
            return <CodeBlock code={textOf(codeNode).replace(/\n$/, '')} lang={lang} title={title} />;
          },
          code: ({ node, className, children }) => <code className="docs-inline-code">{children}</code>,
          li: ({ node, children, ...rest }) => {
            // Cards: first child is the title link → append an arrow
            const kids = Children.toArray(children);
            const isCard = hasCardDesc(node);
            if (isCard && kids.length) {
              return <li className="docs-card" {...rest}>{children}<span className="docs-card-arrow"><ArrowRight size={16} /></span></li>;
            }
            return <li {...rest}>{children}</li>;
          },
          p: ({ node, children }) => {
            // Unwrap a paragraph that only contains an image so <figure> isn't nested in <p>
            const kids = Children.toArray(children);
            if (kids.length === 1 && isValidElement(kids[0]) && node?.children?.[0]?.tagName === 'img') return <>{children}</>;
            return <p>{children}</p>;
          },
        }}
      >
        {content || ''}
      </ReactMarkdown>
    </div>
  );
}
